import React, { useEffect, useState } from 'react'

interface StreakLossProps {
  isOpen: boolean
  onClose: () => void
  previousStreak: number
}

export function StreakLoss({
  isOpen,
  onClose,
  previousStreak
}: StreakLossProps) {
  const [animateIn, setAnimateIn] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setAnimateIn(true)

      const timer = setTimeout(() => {
        onClose()
      }, 5000)

      return () => {
        clearTimeout(timer)
        setAnimateIn(false)
      }
    }

    setAnimateIn(false)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const supportiveMessage =
    previousStreak > 1
      ? `That ${previousStreak}-day run still counts as progress.`
      : 'Every streak begins with day one - today can be yours.'
  const encouragement =
    previousStreak > 1
      ? 'Toko saved you a spot on the lily pad for a fresh hop.'
      : 'Toko is here to practice alongside you.'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div className="relative pointer-events-auto w-full max-w-md px-4">
        <div
          className={`relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-2xl p-8 text-center overflow-hidden ${
            animateIn ? 'animate-scale-in' : 'opacity-0'
          }`}
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onClose()}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-surface-secondary)]/70 via-transparent to-primary-200/30 pointer-events-none" />
          <div className="relative">
            {/* Toko avatar */}
            <div className="flex justify-center -mt-16 mb-4">
              <div className="relative">
                <div className="absolute inset-0 blur-2xl bg-primary-400/20 animate-pulse-soft" />
                <div className="relative w-20 h-20 rounded-full bg-[var(--color-surface-secondary)] border border-[var(--color-border)] shadow-lg flex items-center justify-center">
                  <img
                    src="/frogicon.png"
                    alt="Toko offering encouragement"
                    className="w-12 h-12 opacity-90"
                    draggable={false}
                  />
                </div>
              </div>
            </div>

            <p className="text-xs uppercase tracking-[0.3em] text-[var(--color-text-secondary)] mb-2">
              Fresh start
            </p>
            <h2 className="text-3xl font-semibold text-[var(--color-text)] mb-3">
              Let's hop back in
            </h2>
            <p className="text-[var(--color-text-secondary)] mb-6">
              {supportiveMessage}
            </p>

            <div className="inline-flex items-center justify-center bg-[var(--color-surface-secondary)]/80 border border-[var(--color-border)] rounded-2xl px-6 py-4 shadow-md mb-6">
              <span className="text-4xl font-semibold text-[var(--color-text)]">{previousStreak}</span>
              <span className="text-lg ml-2 font-medium text-[var(--color-text-secondary)]">
                {previousStreak === 1 ? 'day streak' : 'days strong'}
              </span>
            </div>

            <div className="bg-[var(--color-surface-secondary)]/70 border border-[var(--color-border)] rounded-xl p-5 mb-5">
              <p className="text-sm text-[var(--color-text-secondary)] mb-1">
                Gentle reminder
              </p>
              <p className="text-[var(--color-text)] font-medium">
                {encouragement}
              </p>
            </div>

            <p className="text-sm text-[var(--color-text-secondary)]">
              Tap anywhere to begin a new streak.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
